/*
Auto-generated by: https://github.com/pmndrs/gltfjsx
*/

import React, { useRef, useState, useEffect, useMemo } from 'react'
import { useGLTF, Html, Outlines } from '@react-three/drei'
import { useCamera } from './SimpleCameraSystem.jsx'
import { useOverlayManager } from './overlayManager.jsx'
import './unifiedUI.scss'
import * as THREE from 'three'

export function Model({ audioMuted = false, ...props }) {
  const { nodes, materials } = useGLTF('./models/model-scene-portfolio-6.6.0.glb')
  const { isVisible, enterHouse, viewGallery, playMusic, viewAbout, viewContact, enterOffice, currentCameraState, showConversation, showGallery, showMusic, showContact, showAbout, moveToState } = useCamera()
  const { showOverlay, activeOverlay } = useOverlayManager()
  
  // Create doorbell screen material with proper texture settings
  const doorbellScreenMaterial = useMemo(() => {
    if (materials['screen-doorbell']?.map) {
      const mat = materials['screen-doorbell'].clone()
      // Keep original texture but ensure it's not repeating
      if (mat.map) {
        mat.map.repeat.set(1, 1)
        mat.map.center.set(0.5, 0.5)
        mat.map.offset.set(0, 0)
        mat.map.needsUpdate = true
      }
      return mat
    }
    return materials['screen-doorbell']
  }, [materials])
  
  // Tooltip states
  const [showDoorbellTooltip, setShowDoorbellTooltip] = useState(false)
  const [showGalleryTooltip, setShowGalleryTooltip] = useState(false)
  const [showRecordPlayerTooltip, setShowRecordPlayerTooltip] = useState(false)
  const [showMonitorTooltip, setShowMonitorTooltip] = useState(false)
  const [showNotebookTooltip, setShowNotebookTooltip] = useState(false)
  const [showOfficeEntryTooltip, setShowOfficeEntryTooltip] = useState(false)
  
  // Create interaction handlers for target meshes
  const createInteractiveHandlers = (objectId, setTooltip) => ({
    onClick: (event) => {
      event.stopPropagation()
      // Handle different interactions
      if (objectId === 'target-doorbell') {
        // Move camera to doorbell position, then show overlay when camera arrives
        moveToState('doorbell', 1.5, () => showOverlay('intro'))
      } else if (objectId === 'target-gallery') {
        showOverlay('work') // Open work gallery overlay
      } else if (objectId === 'target-music') {
        showOverlay('music') // Open music gallery overlay
      } else if (objectId === 'target-about') {
        showOverlay('about') // Open about gallery overlay
      } else if (objectId === 'target-contact') {
        showOverlay('contact') // Open contact gallery overlay
      } else if (objectId === 'target-office') {
        enterOffice() // Move camera to office position
      }
    },
    onPointerOver: (event) => {
      event.stopPropagation()
      document.body.style.cursor = 'pointer'
      setTooltip(true)
    },
    onPointerOut: (event) => {
      event.stopPropagation()
      document.body.style.cursor = 'default'
      setTooltip(false)
    }
  })
  
  // Interaction handlers for each target mesh
  const doorbellHandlers = createInteractiveHandlers('target-doorbell', setShowDoorbellTooltip)
  const galleryHandlers = createInteractiveHandlers('target-gallery', setShowGalleryTooltip)
  const recordPlayerHandlers = createInteractiveHandlers('target-music', setShowRecordPlayerTooltip)
  const monitorHandlers = createInteractiveHandlers('target-about', setShowMonitorTooltip)
  const notebookHandlers = createInteractiveHandlers('target-contact', setShowNotebookTooltip)
  const officeEntryHandlers = createInteractiveHandlers('target-office', setShowOfficeEntryTooltip)
  
  // Visibility logic - only doorbell is initially visible
  const isDoorbellVisible = isVisible('doorbell')
  const isGalleryVisible = isVisible('gallery')
  const isRecordPlayerVisible = isVisible('recordPlayer')
  const isMonitorVisible = isVisible('monitorScreen')
  const isNotebookVisible = isVisible('notebook')
  const isOfficeEntryVisible = isVisible('officeStairs')
  
  // Hide all interactions when any overlay is open (focus view)
  const shouldShowInteractions = !activeOverlay
  
  // Debug log to see what's visible
  console.log('Visible interactions:', { 
    doorbell: isDoorbellVisible, 
    gallery: isGalleryVisible, 
    recordPlayer: isRecordPlayerVisible,
    monitor: isMonitorVisible,
    notebook: isNotebookVisible
  })
  
  // Audio ref for music player to use (will be controlled by MusicOverlay)
  const audioRef = useRef()

  return (
    <group {...props} dispose={null}>
      {/* STATIC MESHES */}
      <mesh castShadow receiveShadow geometry={nodes.ground.geometry} material={materials.ground} />
      <mesh
        castShadow
        receiveShadow
        geometry={nodes.radio.geometry}
        material={materials['texture-map-1']}
        position={[-0.624, 2.485, -1.736]}
      />
      {/* Props */}
      <group position={[1.929, 1.092, 1.848]} rotation={[0, 0, 1.222]}>
        <mesh
          castShadow
          receiveShadow
          geometry={nodes.Cube001.geometry}
          material={materials['content-phone']}
        />
        <mesh
          castShadow
          receiveShadow
          geometry={nodes.Cube001_1.geometry}
          material={materials['green-matte']}
        />
      </group>
      <group position={[1.872, 1.098, 2.897]} rotation={[0, 0, Math.PI / 2]}>
        <mesh
          castShadow
          receiveShadow
          geometry={nodes.Cylinder002.geometry}
          material={materials['green-matte']}
        />
        <mesh
          castShadow
          receiveShadow
          geometry={nodes.Cylinder002_1.geometry}
          material={materials['airguard-screen']}
        />
        <mesh
          castShadow
          receiveShadow
          geometry={nodes.Cylinder002_2.geometry}
          material={materials['airguard-body']}
        />
        <mesh
          castShadow
          receiveShadow
          geometry={nodes.Cylinder002_3.geometry}
          material={materials['light-airguard']}
        />
      </group>
      <group position={[1.924, 1.107, 3.894]} rotation={[-Math.PI / 2, -1.216, -Math.PI / 2]}>
        <mesh
          castShadow
          receiveShadow
          geometry={nodes.body.geometry}
          material={materials['screen-presense']}
        />
        <mesh
          castShadow
          receiveShadow
          geometry={nodes.body_1.geometry}
          material={materials['presense-body']}
        />
        <mesh
          castShadow
          receiveShadow
          geometry={nodes.body_2.geometry}
          material={materials['green-matte']}
        />
        <mesh
          castShadow
          receiveShadow
          geometry={nodes['light-green'].geometry}
          material={materials.lights}
          position={[0.779, -0.535, 0.046]}
          rotation={[Math.PI / 2, 0, 0]}
        />
        <mesh
          castShadow
          receiveShadow
          geometry={nodes.screen.geometry}
          material={materials['screen-content']}
          position={[0, 0, 0.031]}
          rotation={[Math.PI / 2, 0, 0]}
        />
      </group>
      {/* Baskets and Saxophone */}
      <mesh
        castShadow
        receiveShadow
        geometry={nodes.baskets.geometry}
        material={materials['texture-map-facade']}
        position={[1.751, 0.6, 2.901]}
        rotation={[0, 0, -Math.PI / 2]}
      />
      <mesh
        castShadow
        receiveShadow
        geometry={nodes.saxophone.geometry}
        material={materials['texture-saxophone']}
        position={[1.545, 0.915, -0.787]}
        rotation={[Math.PI / 2, 0, 0.698]}
      />
      <mesh
        castShadow
        receiveShadow
        geometry={nodes.door.geometry}
        material={materials['green-matte']}
        position={[-1.38, 1.408, 4.976]}
      />
      <mesh
        castShadow
        receiveShadow
        geometry={nodes['door-handle'].geometry}
        material={materials['texture-map-facade']}
        position={[-1.38, 1.408, 4.976]}
      />
      <mesh
        castShadow
        receiveShadow
        geometry={nodes['recordPlayer-arm'].geometry}
        material={materials.chrome}
        position={[0.057, 1.29, -1.092]}
      />
      <mesh
        castShadow
        receiveShadow
        geometry={nodes.window.geometry}
        material={materials.window}
        position={[1.148, 2.707, -4.844]}
      />
      <group position={[-0.005, 2.321, 5.09]} scale={[1.893, 0.983, 0.983]}>
        <mesh
          castShadow
          receiveShadow
          geometry={nodes.Cube009.geometry}
          material={materials.chrome}
        />
        <mesh
          castShadow
          receiveShadow
          geometry={nodes.Cube009_1.geometry}
          material={materials.facade}
        />
      </group>
      
      {/* Doorbell Screen - Custom material to prevent stretching */}
      <mesh
        castShadow
        receiveShadow
        geometry={nodes['screen-doorbell'].geometry}
        material={doorbellScreenMaterial}
        position={[-0.005, 1.712, 5.049]}
      />
      {/* Dark overlay for screen when not in doorbell focus */}
      {currentCameraState !== 'doorbell' && (
        <mesh
          geometry={nodes['screen-doorbell'].geometry}
          position={[-0.005, 1.712, 5.049]}
        >
          <meshBasicMaterial color="black" transparent opacity={0.9} />
        </mesh>
      )}
      <mesh
        castShadow
        receiveShadow
        geometry={nodes.plant.geometry}
        material={materials['texture-plant-1']}
        position={[0.187, 2.734, -1.764]}
      />
      <mesh
        castShadow
        receiveShadow
        geometry={nodes.background.geometry}
        material={materials['texture-forest-background-1.0']}
        position={[0, 0, -1.683]}
      />
      <mesh
        castShadow
        receiveShadow
        geometry={nodes['texture-map-interior'].geometry}
        material={materials['texture-map-interior']}
        position={[2.5, 5.021, 0]}
      />
      <mesh
        castShadow
        receiveShadow
        geometry={nodes['texture-map-facade'].geometry}
        material={materials['texture-map-facade']}
        position={[0, 0, 0.449]}
      />
      <mesh
        castShadow
        receiveShadow
        geometry={nodes['interior-chrome'].geometry}
        material={materials.chrome}
        position={[0.292, 1.699, -2.512]}
      />
      <mesh
        castShadow
        receiveShadow
        geometry={nodes.desk.geometry}
        material={materials['texture-map-interior']}
        position={[1.477, 1.45, -3.858]}
      />

      {/* INTERACTIVE ELEMENTS */}
      
      {/* Doorbell - Static */}
      <mesh
        castShadow
        receiveShadow
        geometry={nodes.doorbell.geometry}
        material={materials['texture-doorbell']}
        position={[-0.005, 1.712, 5.049]}
      >
      </mesh>

      {/* Gallery - Static (Visual Only) */}
      <mesh
        castShadow
        receiveShadow
        geometry={nodes.gallery.geometry}
        material={materials['texture-map-gallery']}
        position={[2.285, 1.994, 2.935]}
      >
      </mesh>

      {/* Gallery backing plane */}
      <mesh
        castShadow
        receiveShadow
        geometry={nodes.Plane.geometry}
        material={materials['green-matte']}
        position={[2.285, 0.618, 2.895]}
        rotation={[Math.PI, 0, Math.PI / 2]}
      />

      {/* Record Player Vinyl - Static (Visual Only) */}
      <mesh
        castShadow
        receiveShadow
        geometry={nodes['recordPlayer-vinyl'].geometry}
        material={materials['texture-map-1']}
        position={[-0.252, 1.289, -0.921]}
      >
        {/* Music is now handled by HTML5 audio outside the 3D scene */}
        {isRecordPlayerVisible && (
          <Outlines
            thickness={1}
            color="white"
            transparent
            opacity={0.6}
          />
        )}
      </mesh>

      {/* Monitor Screen - Static (Visual Only) */}
      <mesh
        castShadow
        receiveShadow
        geometry={nodes['screen-desk'].geometry}
        material={materials['screen-desk']}
        position={[1.683, 2.857, -3.847]}
      >
        {isMonitorVisible && (
          <Outlines
            thickness={1}
            color="white"
            transparent
            opacity={0.6}
          />
        )}
      </mesh>

      {/* Notebook - Static (Visual Only) */}
      <mesh
        castShadow
        receiveShadow
        geometry={nodes.notebook.geometry}
        material={materials['texture-map-1']}
        position={[-0.309, 2.471, -1.752]}
      >
        {isNotebookVisible && (
          <Outlines
            thickness={1}
            color="white"
            transparent
            opacity={0.6}
          />
        )}
      </mesh>

      {/* TOUCH TARGETS */}
      
      {/* Target Doorbell - Interactive Touch Zone */}
      <mesh
        geometry={nodes['target-doorbell']?.geometry}
        position={[0, 1.8, 5]}
        scale={1.6}
        visible={false}
        {...doorbellHandlers}
      >
        {isDoorbellVisible && shouldShowInteractions && (
          <Html
            position={[0, -0.5, 0]}
            distanceFactor={6}
            transform={false}
            sprite={false}
          >
            <div className='scene-ui'>
              <div className='doorbell-wrapper'>
                <svg className="chevron-up" viewBox="0 0 24 24">
                  <path d="M7.41,15.41L12,10.83L16.59,15.41L18,14L12,8L6,14L7.41,15.41Z"/>
                </svg>
                <div className={`hover-interactive ${showDoorbellTooltip ? 'visible' : ''}`}>
                  Hello... Someone home?
                </div>
              </div>
            </div>
          </Html>
        )}
      </mesh>

      {/* Target Gallery - Interactive Touch Zone */}


      <mesh
        geometry={nodes['target-gallery']?.geometry}
        position={[2.285, 1.92, 2.898]}
        visible={false}
        {...galleryHandlers}
      >
        {isGalleryVisible && shouldShowInteractions && (
          <Html
            position={[0, 1, 0]}
            distanceFactor={6}
            transform={false}
            sprite={false}
          >
            <div className='scene-ui'>
              <div className='interactive-wrapper'>
                <div className={`hover-interactive ${showGalleryTooltip ? 'visible' : ''}`}>
                  View Gallery
                </div>
                <svg className="chevron-down" viewBox="0 0 24 24">
                  <path d="M7.41,8.58L12,13.17L16.59,8.58L18,10L12,16L6,10L7.41,8.58Z"/>
                </svg>
              </div>
            </div>
          </Html>
        )}
      </mesh>

      {/* Target Music - Interactive Touch Zone */}
      <mesh
        geometry={nodes['target-music']?.geometry}
        position={[-0.252, 1.289, -0.921]}
        visible={false}
        {...recordPlayerHandlers}
      >
        {isRecordPlayerVisible && shouldShowInteractions && (
          <Html
            position={[0, -0.5, 0]}
            distanceFactor={6}
            transform={false}
            sprite={false}
          >
            <div className='scene-ui'>
              <div className='interactive-wrapper'>
                <svg className="chevron-up" viewBox="0 0 24 24">
                  <path d="M7.41,15.41L12,10.83L16.59,15.41L18,14L12,8L6,14L7.41,15.41Z"/>
                </svg>
                <div className={`hover-interactive ${showRecordPlayerTooltip ? 'visible' : ''}`}>
                  Play Music
                </div>
              </div>
            </div>
          </Html>
        )}
      </mesh>

      {/* Target About - Interactive Touch Zone */}
      <mesh
        geometry={nodes['target-about']?.geometry}
        position={[1.655, 2.8, -3.875]}
        visible={false}
        {...monitorHandlers}
      >
        {isMonitorVisible && shouldShowInteractions && (
          <Html
            position={[0, -0.5, 0]}
            distanceFactor={6}
            transform={false}
            sprite={false}
          >
            <div className='scene-ui'>
              <div className='interactive-wrapper'>
                <svg className="chevron-up" viewBox="0 0 24 24">
                  <path d="M7.41,15.41L12,10.83L16.59,15.41L18,14L12,8L6,14L7.41,15.41Z"/>
                </svg>
                <div className={`hover-interactive ${showMonitorTooltip ? 'visible' : ''}`}>
                  About
                </div>
              </div>
            </div>
          </Html>
        )}
      </mesh>

      {/* Target Contact - Interactive Touch Zone */}
      <mesh
        geometry={nodes['target-contact']?.geometry}
        position={[-0.309, 2.471, -1.752]}
        visible={false}
        {...notebookHandlers}
      >
        {isNotebookVisible && shouldShowInteractions && (
          <Html
            position={[0, -0.5, 0]}
            distanceFactor={6}
            transform={false}
            sprite={false}
          >
            <div className='scene-ui'>
              <div className='interactive-wrapper'>
                <svg className="chevron-up" viewBox="0 0 24 24">
                  <path d="M7.41,15.41L12,10.83L16.59,15.41L18,14L12,8L6,14L7.41,15.41Z"/>
                </svg>
                <div className={`hover-interactive ${showNotebookTooltip ? 'visible' : ''}`}>
                  Contact
                </div>
              </div>
            </div>
          </Html>
        )}
      </mesh>

      {/* Target Office Entry - Interactive Touch Zone */}
      <mesh
        geometry={nodes['target-office']?.geometry}
        position={nodes['target-office']?.position}
        rotation={nodes['target-office']?.rotation}
        scale={nodes['target-office']?.scale}
        visible={false}
        {...officeEntryHandlers}
      >
        {isOfficeEntryVisible && shouldShowInteractions && (
          <Html
            position={[0, -0.5, 0]}
            distanceFactor={6}
            transform={false}
            sprite={false}
          >
            <div className='scene-ui' style={{pointerEvents: 'none'}}>
              <div className='office-entry-wrapper' style={{pointerEvents: 'none'}}>
                <svg className="office-input-icon" viewBox="0 0 24 24" style={{transform: 'rotate(-90deg) scaleY(-1)', width: '40px', height: '40px', fill: 'white', pointerEvents: 'none'}}>
                  <path d="M12 2L16 6H13V12H11V6H8L12 2ZM5 17V19H19V17H5Z"/>
                </svg>
                <div className={`hover-interactive ${showOfficeEntryTooltip ? 'visible' : ''}`} style={{pointerEvents: 'none'}}>
                  to office
                </div>
              </div>
            </div>
          </Html>
        )}
      </mesh>
    </group>
  )
}

useGLTF.preload('./models/model-scene-portfolio-6.6.0.glb')

